import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getPageContent, subscribeToPage } from '../firebase/firebase-config';

function TimeTable() {
  const [content, setContent] = useState({ 
    selectedClass: '', 
    selectedSection: '', 
    timetableFiles: {},
    title: 'Time Table',
    schoolTimings: {
      mondayToFriday: '8:00 AM - 2:00 PM',
      saturday: '8:00 AM - 12:30 PM'
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await getPageContent('timeTable');
        setContent(prev => ({ ...prev, ...data }));
      } catch (error) { 
        console.error(error); 
      } finally { 
        setLoading(false); 
      }
    };
    loadData();
    const unsubscribe = subscribeToPage('timeTable', (data) => { 
      if (data) setContent(prev => ({ ...prev, ...data })); 
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading Time Table...</p>
      </div>
    );
  }

  const timetableFiles = content.timetableFiles || {};
  
  // Get available sections for selected class
  const availableSections = content.selectedClass && timetableFiles[content.selectedClass] 
    ? Object.keys(timetableFiles[content.selectedClass]).sort()
    : [];

  // Get the current timetable file
  const currentFile = content.selectedClass && content.selectedSection 
    ? timetableFiles[content.selectedClass]?.[content.selectedSection]
    : null;

  // All classes list for dropdown (including those with timetables)
  const allClasses = ['Nursery', 'LKG', 'UKG', ...Array(12).fill().map((_, i) => `Class ${i + 1}`), 'Class 12'];

  const handlePrint = () => {
    if (!currentFile) {
      alert('No timetable available to print.');
      return;
    }
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Time Table - ${content.selectedClass} - Section ${content.selectedSection}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; text-align: center; }
            h2 { color: #1a3a6b; margin-bottom: 10px; }
            .info { color: #64748b; margin-bottom: 20px; }
            img { max-width: 100%; height: auto; box-shadow: 0 4px 20px rgba(0,0,0,0.1); border-radius: 8px; }
            @media print {
              body { padding: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <h2>${content.selectedClass} - Section ${content.selectedSection}</h2>
          <div class="info">Academic Session: ${new Date().getFullYear()}-${new Date().getFullYear() + 1}</div>
          <img src="${currentFile.url}" alt="Time Table" />
          <div class="no-print" style="margin-top: 20px;">
            <button onclick="window.print();window.close();" style="padding: 10px 20px; background: #1a3a6b; color: white; border: none; border-radius: 5px; cursor: pointer;">Print Time Table</button>
          </div>
          <script>
            window.onload = function() { setTimeout(() => { window.print(); }, 500); }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="timetable-page">
      {/* Hero Section */}
      
        <div className="container">
          <h1>{content.title}</h1>
          <div className="hero-divider"></div>
          <p>Class-wise Time Table • View & Print • Easy Access</p>
        </div>
      

      <div className="container">
        

        {/* Selection Section */}
        <div className="selection-section">
          <div className="selector-group">
            <label>Select Class</label>
            <select 
              value={content.selectedClass} 
              onChange={(e) => setContent({...content, selectedClass: e.target.value, selectedSection: ''})}
              className="selector-select"
            >
              <option value="">-- Select Class --</option>
              {allClasses.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div className="selector-group">
            <label>Select Section</label>
            <select 
              value={content.selectedSection} 
              onChange={(e) => setContent({...content, selectedSection: e.target.value})}
              className="selector-select"
              disabled={!content.selectedClass}
            >
              <option value="">-- Select Section --</option>
              {availableSections.length > 0 ? (
                availableSections.map(s => (
                  <option key={s} value={s}>Section {s}</option>
                ))
              ) : (
                <option value="" disabled>No sections available</option>
              )}
            </select>
          </div>
        </div>

        {/* Timetable Display */}
        {content.selectedClass && content.selectedSection && (
          <div className="timetable-display">
            <div className="timetable-header">
              <h2>{content.selectedClass} - Section {content.selectedSection}</h2>
              {currentFile && (
                <button className="print-btn" onClick={handlePrint}>
                  🖨️ Print Timetable
                </button>
              )}
            </div>

            {currentFile && currentFile.url ? (
              <div className="timetable-image-container">
                <img 
                  src={currentFile.url} 
                  alt={`Time Table for ${content.selectedClass} - Section ${content.selectedSection}`}
                  className="timetable-image"
                  onClick={() => window.open(currentFile.url, '_blank')}
                />
                <div className="image-overlay">
                  <span>🔍 Click to view full size</span>
                </div>
              </div>
            ) : (
              <div className="no-timetable">
                <div className="no-timetable-icon">📅</div>
                <h3>No Timetable Available</h3>
                <p>Timetable for {content.selectedClass} - Section {content.selectedSection} has not been uploaded yet.</p>
                <p className="contact-info">Please check back later or contact the school office.</p>
              </div>
            )}
          </div>
        )}

        {/* Info Cards */}
        <div className="info-section">
          <div className="info-card">
            <div className="info-icon">📋</div>
            <h4>Important Notes</h4>
            <p>• Timetables are subject to change</p>
            <p>• Students are advised to check for updates regularly</p>
            <p>• Contact class teacher for any queries</p>
          </div>
          <div className="info-card">
            <div className="info-icon">🖨️</div>
            <h4>Print Timetable</h4>
            <p>• Click the Print button to take a printout</p>
            <p>• You can also download the image</p>
            <p>• Keep a copy for reference</p>
          </div>
        </div>

        {/* Back to Academics Link */}
        <div className="back-link">
          <Link to="/academics" className="back-btn">
            ← Back to Academics
          </Link>
        </div>
      </div>

      <style>{`
        .timetable-page {
          background: #f8fafc;
          min-height: 100vh;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 60vh;
        }

        .loading-spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #e2e8f0;
          border-top-color: #1a3a6b;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .container {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 24px 60px;
        }

        /* Page Hero */
        .page-hero {
          background: linear-gradient(135deg, #1a3a6b, #f59e0b);
          color: white;
          text-align: center;
          padding: 60px 20px;
          margin-bottom: 30px;
        }

        .page-hero h1 {
          font-size: 48px;
          margin-bottom: 20px;
          font-weight: 700;
        }

        .hero-divider {
          width: 60px;
          height: 4px;
          background: white;
          margin: 0 auto 20px;
          border-radius: 2px;
        }

        .page-hero p {
          font-size: 18px;
          opacity: 0.9;
        }

        /* School Timings Banner */
        .school-timings-banner {
          background: #1a3a6b;
          border-radius: 12px;
          padding: 15px 20px;
          margin-bottom: 30px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .timings-content {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          color: white;
          flex-wrap: wrap;
        }

        .timings-icon {
          font-size: 24px;
        }

        .timings-text {
          font-size: 15px;
          line-height: 1.5;
        }

        .timings-text strong {
          color: #f59e0b;
          margin-right: 8px;
        }

        /* Selection Section */
        .selection-section {
          display: flex;
          gap: 30px;
          justify-content: center;
          margin-bottom: 40px;
          flex-wrap: wrap;
        }

        .selector-group {
          flex: 1;
          min-width: 250px;
        }

        .selector-group label {
          display: block;
          margin-bottom: 8px;
          font-weight: 600;
          color: #334155;
          font-size: 14px;
        }

        .selector-select {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          font-size: 16px;
          background: white;
          cursor: pointer;
          transition: all 0.3s;
        }

        .selector-select:hover,
        .selector-select:focus {
          border-color: #1a3a6b;
          outline: none;
        }

        .selector-select:disabled {
          background: #f1f5f9;
          cursor: not-allowed;
        }

        /* Timetable Display */
        .timetable-display {
          background: white;
          border-radius: 24px;
          padding: 30px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          margin-bottom: 40px;
        }

        .timetable-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 25px;
          flex-wrap: wrap;
          gap: 15px;
          padding-bottom: 15px;
          border-bottom: 2px solid #e2e8f0;
        }

        .timetable-header h2 {
          color: #1a3a6b;
          font-size: 24px;
          font-weight: 700;
          margin: 0;
        }

        .print-btn {
          background: #1a3a6b;
          color: white;
          border: none;
          padding: 10px 24px;
          border-radius: 40px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.3s;
        }

        .print-btn:hover {
          background: #f59e0b;
          transform: translateY(-2px);
        }

        .timetable-image-container {
          position: relative;
          border-radius: 16px;
          overflow: hidden;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          background: #f8fafc;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .timetable-image {
          width: 100%;
          height: auto;
          display: block;
          transition: transform 0.3s;
        }

        .timetable-image:hover {
          transform: scale(1.02);
        }

        .image-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: rgba(0, 0, 0, 0.7);
          color: white;
          text-align: center;
          padding: 12px;
          font-size: 14px;
          transform: translateY(100%);
          transition: transform 0.3s;
        }

        .timetable-image-container:hover .image-overlay {
          transform: translateY(0);
        }

        /* No Timetable */
        .no-timetable {
          text-align: center;
          padding: 60px 20px;
          background: #f8fafc;
          border-radius: 16px;
        }

        .no-timetable-icon {
          font-size: 64px;
          margin-bottom: 20px;
        }

        .no-timetable h3 {
          color: #1a3a6b;
          margin-bottom: 10px;
          font-size: 20px;
        }

        .no-timetable p {
          color: #64748b;
          margin-bottom: 8px;
        }

        .contact-info {
          margin-top: 15px;
          font-size: 14px;
          color: #f59e0b;
        }

        /* Info Section */
        .info-section {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 25px;
          margin-bottom: 40px;
        }

        .info-card {
          background: white;
          padding: 25px;
          border-radius: 20px;
          text-align: center;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          transition: transform 0.3s;
        }

        .info-card:hover {
          transform: translateY(-3px);
        }

        .info-icon {
          font-size: 40px;
          margin-bottom: 15px;
        }

        .info-card h4 {
          color: #1a3a6b;
          font-size: 18px;
          margin-bottom: 15px;
        }

        .info-card p {
          color: #64748b;
          font-size: 14px;
          line-height: 1.8;
          margin: 8px 0;
        }

        /* Back Link */
        .back-link {
          text-align: center;
          margin-top: 20px;
        }

        .back-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #e2e8f0;
          color: #475569;
          padding: 12px 28px;
          border-radius: 40px;
          text-decoration: none;
          font-weight: 600;
          transition: all 0.3s;
        }

        .back-btn:hover {
          background: #1a3a6b;
          color: white;
          transform: translateX(-5px);
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .container {
            padding: 0 20px 50px;
          }
        }

        @media (max-width: 768px) {
          .page-hero {
            padding: 50px 20px;
          }
          
          .page-hero h1 {
            font-size: 32px;
          }
          
          .page-hero p {
            font-size: 16px;
          }

          .school-timings-banner {
            padding: 12px 16px;
          }

          .timings-text {
            font-size: 13px;
            text-align: center;
          }

          .selection-section {
            flex-direction: column;
            gap: 15px;
          }

          .timetable-display {
            padding: 20px;
          }

          .timetable-header {
            flex-direction: column;
            text-align: center;
          }

          .timetable-header h2 {
            font-size: 20px;
          }

          .info-section {
            grid-template-columns: 1fr;
            gap: 20px;
          }
        }

        @media (max-width: 480px) {
          .page-hero h1 {
            font-size: 28px;
          }

          .timetable-display {
            padding: 15px;
          }

          .info-card {
            padding: 20px;
          }
        }
      `}</style>
    </div>
  );
}

export default TimeTable;